import { getAiResponse, getAiResponseWithImageAsInput } from '../../api/getAiResponse';
import { ChatMessage } from '../../types/Message.types';
import { getChatStore } from '../../state/useChatState';
import { useAuth } from '../../feature-auth/context/AuthContext';
import { useMemo, useState } from 'react';

export function useChatHandlers() {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState<string | undefined>();
  
  // Get the store for the current user
  const chatStore = useMemo(() => {
    if (!user) return null;
    return getChatStore(user.uid);
  }, [user]);

  if (!chatStore) {
    return {
      messages: [],
      isLoading: false,
      handleSendMessage: async () => {},
      handleSendMessageWithFile: async () => {},
      handleClearMessages: () => {},
      error: null,
      setError: () => {},
    };
  }

  const messages = chatStore((state) => state.messages);
  const setMessages = chatStore((state) => state.setMessages);
  const isLoading = chatStore((state) => state.isLoading);
  const setIsLoading = chatStore((state) => state.setIsLoading);
  const error = chatStore((state) => state.error);
  const setError = chatStore((state) => state.setError);

  const handleSendMessage = async (message: ChatMessage) => {
    setMessages([...messages, message]);
    setIsLoading(true);
    try {
      const { response, sessionId: newSessionId } = await getAiResponse(message.text, sessionId);
      setSessionId(newSessionId);
      const aiMessage: ChatMessage = { fromUser: false, text: response };
      setMessages([...messages, message, aiMessage]);
    } catch (error) {
      console.error("Fehler beim Abrufen der Antwort:", error);
      setError("Beim Abrufen der Antwort ist ein Fehler aufgetreten.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessageWithFile = async (message: ChatMessage) => {
    setMessages([...messages, message]);
    setIsLoading(true);
    try {
      const { response, sessionId: newSessionId } = await getAiResponseWithImageAsInput(
        message.text, 
        message.imageUrl || '',
        sessionId
      );
      setSessionId(newSessionId);
      const aiMessage: ChatMessage = { fromUser: false, text: response };
      setMessages([...messages, message, aiMessage]);
    } catch (error) {
      console.error("Fehler beim Abrufen der Antwort:", error);
      setError("Beim Abrufen der Antwort ist ein Fehler aufgetreten.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearMessages = () => {
    setMessages([]);
    setIsLoading(false);
    setError(null);
    setSessionId(undefined);
    if (user) {
      localStorage.removeItem(`chat-storage-${user.uid}`);
    }
  };

  return {
    messages,
    isLoading,
    handleSendMessage,
    handleSendMessageWithFile,
    handleClearMessages,
    error,
    setError,
  };
}